import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { Textarea } from "~/atoms/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import i18n from "~/i18n";
import { useUserContext } from "~/hooks/useUserContext";
import { PlatformRole } from "~/config/types";
import { DialogFooter } from "~/atoms/dialog";
import { ConfirmDeletePopover } from "./ConfirmDeletePopover";

const FormSchema = z.object({
  title: z.string().min(1, i18n.t("validations.required")),
  description: z.string().optional(),
});

export type FormValues = z.infer<typeof FormSchema>;

interface Props {
  data?: {
    title: string;
    description?: string;
    details: { documents: number };
  };
  onDelete?: () => Promise<void>;
  onSubmit: (values: FormValues) => Promise<void>;
}

export const TitleAndDescriptionForm = ({
  data,
  onDelete,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const form = useForm({
    resolver: zodResolver(FormSchema),
    values: {
      title: data?.title ?? "",
      description: data?.description ?? "",
    },
  });

  const isReader = user.role === PlatformRole.reader;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("modal.categoryTierModal.titleLabel")}</FormLabel>
              <FormControl>
                <Input
                  readOnly={isReader}
                  placeholder={t("modal.categoryTierModal.titlePlaceholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("modal.categoryTierModal.descriptionLabel")}
              </FormLabel>
              <FormControl>
                <Textarea
                  readOnly={isReader}
                  placeholder={t(
                    "modal.categoryTierModal.descriptionPlaceholder",
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isReader && (
          <DialogFooter>
            {onDelete && (
              <ConfirmDeletePopover
                trigger={
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!!data?.details.documents}
                  >
                    {t("modal.categoryTierModal.delete")}
                  </Button>
                }
                onConfirm={onDelete}
              />
            )}
            <Button type="submit">
              {data
                ? t("modal.categoryTierModal.update")
                : t("modal.categoryTierModal.create")}
            </Button>
          </DialogFooter>
        )}
      </form>
    </Form>
  );
};
